import { useSyncExternalStore } from "react";
import { wsUrl } from "../config";
import type { ServerFrame } from "./types";

export type SocketStatus = "idle" | "connecting" | "open" | "reconnecting" | "closed";

/** Reconnect delays from contract section 6, in seconds. */
export const BACKOFF_SECONDS = [1, 2, 5, 10, 30] as const;

export function backoffDelayMs(attempt: number): number {
  const i = Math.min(attempt, BACKOFF_SECONDS.length - 1);
  return (BACKOFF_SECONDS[i] ?? 30) * 1000;
}

type FrameListener = (frame: ServerFrame) => void;
type StatusListener = () => void;

interface SocketState {
  status: SocketStatus;
  retryAt: number | null;
  connectedAt: number | null;
}

/**
 * One WebSocket per console tab. Auth is the session cookie (same origin), so no token in the URL.
 * Subscribes to the `logs` topic on every (re)connect and tells listeners when it reconnected so
 * they can refetch.
 */
export class LiveSocket {
  private ws: WebSocket | null = null;
  private attempt = 0;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private frameListeners = new Set<FrameListener>();
  private statusListeners = new Set<StatusListener>();
  private reconnectListeners = new Set<() => void>();
  private wanted = false;
  private everOpened = false;
  /** Topics besides `logs`, re-sent on every (re)connect. Counted so two screens can share one. */
  private extra = new Map<string, number>();
  state: SocketState = { status: "idle", retryAt: null, connectedAt: null };

  constructor(private readonly url: () => string) {}

  start(): void {
    if (this.wanted) return;
    this.wanted = true;
    this.connect();
  }

  stop(): void {
    this.wanted = false;
    this.clearTimers();
    this.ws?.close(1000);
    this.ws = null;
    this.setState({ status: "closed", retryAt: null, connectedAt: null });
  }

  /** Skip the remaining backoff and try now. */
  retryNow(): void {
    if (!this.wanted || this.state.status === "open" || this.state.status === "connecting") return;
    this.clearTimers();
    this.connect();
  }

  /** Subscribes to a topic (for example `live`) until the returned function is called. */
  subscribe(topic: string): () => void {
    const n = this.extra.get(topic) ?? 0;
    this.extra.set(topic, n + 1);
    if (n === 0) this.send({ type: "subscribe", data: { topics: [topic] } });
    return () => {
      const left = (this.extra.get(topic) ?? 1) - 1;
      if (left > 0) {
        this.extra.set(topic, left);
        return;
      }
      this.extra.delete(topic);
      this.send({ type: "unsubscribe", data: { topics: [topic] } });
    };
  }

  private send(frame: unknown): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(frame));
  }

  onFrame(fn: FrameListener): () => void {
    this.frameListeners.add(fn);
    return () => this.frameListeners.delete(fn);
  }

  onStatus(fn: StatusListener): () => void {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  onReconnect(fn: () => void): () => void {
    this.reconnectListeners.add(fn);
    return () => this.reconnectListeners.delete(fn);
  }

  private setState(next: SocketState): void {
    this.state = next;
    for (const fn of this.statusListeners) fn();
  }

  private clearTimers(): void {
    if (this.retryTimer) clearTimeout(this.retryTimer);
    if (this.pingTimer) clearInterval(this.pingTimer);
    this.retryTimer = null;
    this.pingTimer = null;
  }

  private connect(): void {
    this.setState({
      status: this.attempt === 0 ? "connecting" : "reconnecting",
      retryAt: null,
      connectedAt: null,
    });
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url());
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      const isReconnect = this.everOpened;
      this.everOpened = true;
      this.attempt = 0;
      this.setState({ status: "open", retryAt: null, connectedAt: Date.now() });
      ws.send(JSON.stringify({ type: "subscribe", data: { topics: ["logs", ...this.extra.keys()] } }));
      this.pingTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: "ping" }));
      }, 25_000);
      if (isReconnect) for (const fn of this.reconnectListeners) fn();
    };
    ws.onmessage = (ev) => {
      let frame: ServerFrame;
      try {
        frame = JSON.parse(typeof ev.data === "string" ? ev.data : "") as ServerFrame;
      } catch {
        return;
      }
      if (!frame || typeof frame.type !== "string") return;
      for (const fn of this.frameListeners) fn(frame);
    };
    ws.onclose = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      if (this.pingTimer) clearInterval(this.pingTimer);
      this.pingTimer = null;
      if (this.wanted) this.scheduleRetry();
    };
    ws.onerror = () => {
      // onclose follows; retry is scheduled there.
    };
  }

  private scheduleRetry(): void {
    const delay = backoffDelayMs(this.attempt);
    this.attempt += 1;
    this.setState({
      status: "reconnecting",
      retryAt: Date.now() + delay,
      connectedAt: null,
    });
    this.retryTimer = setTimeout(() => this.connect(), delay);
  }
}

export const liveSocket = new LiveSocket(() => wsUrl("/v1/ws"));

export function useSocketState(socket: LiveSocket = liveSocket): SocketState {
  return useSyncExternalStore(
    (cb) => socket.onStatus(cb),
    () => socket.state,
    () => socket.state,
  );
}
